'use client';
import { AccordionController } from '@volvo-cars/react-accordion';
import { useAnimatedDetails } from '@volvo-cars/react-headless';
/**
 * Renders a list of frequently asked questions (FAQs) in an accordion format.
 *
 * @component
 * @example
 * ```tsx
 * <Faqs
 *   initialLength={3}
 *   showMoreText="Show More"
 *   showLessText="Show Less"
 *   exclusive={true}
 * >
 *     <Faq trackEventLabel="FAQ 1">
 *       <Question>Question 1</Question>
 *       <Answer>Answer 1</Answer>
 *     </Faq>
 *     <Faq trackEventLabel="FAQ 2">
 *       <Question>Question 2</Question>
 *       <Answer>Answer 2</Answer>
 *     </Faq>
 * </Faqs>
 * ```
 *
 * @param {Object} props - The component props.
 * @returns {JSX.Element} The rendered component.
 */
import React, { useId, useState } from 'react';
import ExpandedButton from './ExpandedButton';

/**
 * Props for the Faqs component.
 */
export type Props = {
  /**
   * The initial length of the faqs to display.
   */
  initialLength?: number;

  /**
   * The text to display for the "Show More" button.
   */
  showMoreText?: string;

  /**
   * The text to display for the "Show Less" button.
   */
  showLessText?: string;

  /**
   * Whether only one faq can be expanded at a time.
   */
  exclusive?: boolean;

  /**
   * Data source for the "Show more" button.
   */
  showMoreSource?: string;

  /**
   * Data source for the "Show less" button.
   */
  showLessSource?: string;
};

const Faqs: React.FC<React.PropsWithChildren<Props>> = ({
  children,
  initialLength,
  showMoreText,
  showLessText,
  exclusive,
  showMoreSource,
  showLessSource,
}) => {
  const [showMoreExpanded, setShowMoreExpanded] = useState(false);
  const toggleId = useId();
  const toggleProps = useAnimatedDetails({
    open: showMoreExpanded,
    onToggle: setShowMoreExpanded,
  });
  const faqs = Array.isArray(children) ? children : [children];

  const delimiter = initialLength || faqs.length;
  const initialFaqs = faqs.slice(0, delimiter);
  const hiddenFaqs = faqs.slice(delimiter);

  return (
    <AccordionController exclusive={exclusive}>
      {initialFaqs}
      {hiddenFaqs.length ? (
        <>
          <details {...toggleProps} id={toggleId}>
            <summary hidden />
            {hiddenFaqs}
          </details>
          <ExpandedButton
            showMoreExpanded={showMoreExpanded}
            setShowMoreExpanded={setShowMoreExpanded}
            toggleId={toggleId}
            showMoreText={showMoreText}
            showLessText={showLessText}
            showMoreSource={showMoreSource}
            showLessSource={showLessSource}
          />
        </>
      ) : null}
    </AccordionController>
  );
};

export default Faqs;
